import { Controller } from "@hotwired/stimulus";

// Handles contact form character counter and Turnstile integration
export default class extends Controller {
  static targets = [
    "message",
    "counter",
    "submitButton",
    "turnstileResponse",
    "status",
  ];
  static values = {
    isDevelopment: Boolean,
    turnstileConfigured: Boolean,
  };

  connect() {
    // Store controller instance globally for Turnstile callbacks
    window.contactFormController = this;

    // Initialize character counter
    this.updateCounter();

    // Store bound handler for cleanup
    this._handlePageShow = (event) => {
      if (event.persisted) {
        // Page was loaded from browser cache (back button)
        this.element.reset();
        this.updateCounter();
      }
    };

    // Clear form when navigating back from thank you page
    // This prevents form values from persisting in browser cache
    window.addEventListener("pageshow", this._handlePageShow);

    // Enable submit button in development if Turnstile not configured
    if (!this.turnstileConfiguredValue && this.isDevelopmentValue) {
      setTimeout(() => {
        if (this.hasSubmitButtonTarget) {
          this.submitButtonTarget.disabled = false;
          console.log("Development mode: Submit button enabled");
        }
      }, 500);
    }

    // Check if Turnstile loaded (if configured)
    if (this.turnstileConfiguredValue) {
      window.addEventListener("load", () => {
        setTimeout(() => {
          this.checkTurnstileLoaded();
        }, 5000);
      });
    }
  }

  disconnect() {
    // Remove pageshow event listener
    if (this._handlePageShow) {
      window.removeEventListener("pageshow", this._handlePageShow);
    }

    // Clean up global reference
    window.contactFormController = null;
  }

  // Update character counter
  updateCounter() {
    if (!this.hasMessageTarget || !this.hasCounterTarget) return;

    const length = this.messageTarget.value.length;
    this.counterTarget.textContent = length;

    // Change color when approaching limit
    const counterParent = this.counterTarget.parentElement;
    if (length > 4900) {
      counterParent.classList.add("text-red-500");
      counterParent.classList.remove("text-neutral-500");
    } else {
      counterParent.classList.remove("text-red-500");
      counterParent.classList.add("text-neutral-500");
    }
  }

  // Turnstile success callback (called globally, needs to be accessible)
  turnstileSuccess(token) {
    console.log("Turnstile verification successful");

    if (this.hasTurnstileResponseTarget) {
      this.turnstileResponseTarget.value = token;
    }

    if (this.hasSubmitButtonTarget) {
      this.submitButtonTarget.disabled = false;
    }

    if (this.hasStatusTarget) {
      this.statusTarget.textContent = "Verification complete!";
    }
  }

  // Turnstile error callback
  turnstileError() {
    console.error("Turnstile verification failed");

    if (this.hasStatusTarget) {
      this.statusTarget.textContent =
        "Verification failed. Please refresh and try again.";
    }

    // In development/test, enable the button anyway
    if (this.isDevelopmentValue && this.hasSubmitButtonTarget) {
      this.submitButtonTarget.disabled = false;
      console.log(
        "Development mode: Enabling submit button despite Turnstile error",
      );
    }
  }

  // Turnstile expired callback
  turnstileExpired() {
    console.log("Turnstile token expired");

    if (this.hasTurnstileResponseTarget) {
      this.turnstileResponseTarget.value = "";
    }

    if (this.hasSubmitButtonTarget) {
      this.submitButtonTarget.disabled = true;
    }

    if (this.hasStatusTarget) {
      this.statusTarget.textContent =
        "Verification expired. Please complete the challenge again.";
    }
  }

  // Check if Turnstile widget loaded successfully
  checkTurnstileLoaded() {
    const turnstileElement = document.querySelector(".cf-turnstile");

    if (turnstileElement && !window.turnstile) {
      console.error("Turnstile failed to load");

      if (this.hasStatusTarget) {
        this.statusTarget.textContent =
          "Security verification unavailable. Please try again later.";
      }

      // In development, enable submit anyway
      if (this.isDevelopmentValue && this.hasSubmitButtonTarget) {
        this.submitButtonTarget.disabled = false;
        console.log(
          "Development mode: Enabling submit due to Turnstile load failure",
        );
      }
    }
  }
}

// Make Turnstile callbacks globally accessible
// These will be called by Turnstile widget and use the stored controller instance
window.onTurnstileSuccess = function (token) {
  if (window.contactFormController) {
    window.contactFormController.turnstileSuccess(token);
  }
};

window.onTurnstileError = function () {
  if (window.contactFormController) {
    window.contactFormController.turnstileError();
  }
};

window.onTurnstileExpired = function () {
  if (window.contactFormController) {
    window.contactFormController.turnstileExpired();
  }
};
